import { useCallback, useMemo } from 'react';

import { isToday } from 'date-fns';
import { useAtomValue } from 'jotai';

import type { EventName } from '@/utils/mixpanel';
import { useTrackEventWithOptionalParams } from '@/utils/mixpanel';

import type { VideaNotesPatient } from '../context';
import {
  availableAudioDevicesAtom,
  hasMicrophonePermissionAtom,
  previewAudioStreamAtom,
  selectedAudioDeviceIdAtom,
} from '../state';
import type { VoiceNotesSource } from '../types';

interface UseVideaNotesAnalyticsParams {
  patient: VideaNotesPatient | undefined;
  estimatedVisitId: number | string | null | undefined;
  unifiedAppointmentId: string | undefined;
  standalonePatientVisitId: string | undefined;
  selectedPrompt: string | undefined;
  selectedDate: Date | null;
  voiceNotesSource: VoiceNotesSource;
}

/**
 * Hook for tracking Videa Notes analytics events.
 * Automatically includes microphone state and patient context in all events.
 */
export const useVideaNotesAnalytics = ({
  patient,
  estimatedVisitId,
  unifiedAppointmentId,
  standalonePatientVisitId,
  selectedPrompt,
  selectedDate,
  voiceNotesSource,
}: UseVideaNotesAnalyticsParams) => {
  const { trackEvent } = useTrackEventWithOptionalParams();

  // Read microphone atoms internally - these are only needed for analytics
  const hasMicrophonePermission = useAtomValue(hasMicrophonePermissionAtom);
  const availableDevices = useAtomValue(availableAudioDevicesAtom);
  const selectedDeviceId = useAtomValue(selectedAudioDeviceIdAtom);
  const previewStream = useAtomValue(previewAudioStreamAtom);
  const isStreamDetected = previewStream?.active === true;

  // Build analytics payload internally
  const eventTrackingPayload = useMemo(
    () => ({
      patient: {
        imagingPatientId: patient?.imagingPatientId,
        pmsPatientId: patient?.pmsPatientId,
        videaId: patient?.videaId,
      },
      hasMicrophonePermission,
      availableDevices,
      selectedDeviceId: selectedDeviceId || '',
      isStreamDetected,
      estimatedVisitId,
      unifiedAppointmentId,
      standalonePatientVisitId,
      template: selectedPrompt,
      date: selectedDate,
      isToday: selectedDate ? isToday(selectedDate) : false,
      voiceNotesSource,
    }),
    [
      patient,
      hasMicrophonePermission,
      availableDevices,
      selectedDeviceId,
      isStreamDetected,
      estimatedVisitId,
      unifiedAppointmentId,
      standalonePatientVisitId,
      selectedPrompt,
      selectedDate,
      voiceNotesSource,
    ],
  );

  const trackVideaNotesEvent = useCallback(
    (event: EventName, additionalPayload?: Record<string, unknown>) => {
      trackEvent(event, { ...eventTrackingPayload, ...additionalPayload });
    },
    [trackEvent, eventTrackingPayload],
  );

  return { trackVideaNotesEvent };
};
